import { Trans } from '@lingui/macro'
import {
  configurationTypes
} from 'app/services/sfAuth/sfData/sfForms'
import { ReactElement, useEffect, useState } from 'react'
import { basePortalConfig } from '../internal/ConfigurePortalConfigurations'
import { Configuration, ConfigurationValue } from 'app/backend/salesforce-maps/Configuration'
import { queryGetObjects } from 'app/backend/Common'
import { isValidJSON } from 'utils'
import { ReduxPortalConfig } from 'app/redux/ReduxInterfaces'

interface GetFormUsageParams {
  formId: string
  configuration: ReduxPortalConfig
  langVersion: string
}

/** Lookup object for labels of application type configurations in which the form is used */
export const applicationTypeConfigurationFormFieldNames = {
  form: <Trans>Form</Trans>,
  quiz: <Trans>Quiz</Trans>,
  printForm: <Trans>Print form</Trans>,
  granteeReportForm: <Trans>Grantee report form (preview)</Trans>,
  internalPrintPreview: <Trans>Applications list print preview</Trans>,
  contract: <Trans>Contract</Trans>,
  previewPM: <Trans>Contract opportunity preview</Trans>,
  SC: <Trans>Selection Committee preview</Trans>,
  AC: <Trans>Allocation Committee preview</Trans>,
  Board: <Trans>Board executive summary preview</Trans>,
  CMHC: <Trans>CMHC executive summary preview</Trans>
}

/** Array of field names for application type configurations in which the form is used */
export const applicationTypeConfigurationFormFields = Object.keys(
  applicationTypeConfigurationFormFieldNames
)

/** This function filters configurations to leave only the ones that are needed to configure application types
 * @param {Array} configurations
 * @returns {Array} configurations
 */
const filterConfigurations = (configurations: Configuration[]): Configuration[] => {
  const filtered = configurations.filter(item => {
    let data: ConfigurationValue = { type: '' }
    const isValid = isValidJSON(item.value)
    if (isValid) {
      data = JSON.parse(item.value) ?? {}
    }
    return (
      configurationTypes[data.type] ?? item.name === 'AVAILABLE_APPLICATIONS'
    )
  })
  return filtered
}

/** This function returns an array of portal configurations labels that are associated with the formId.
 * @param {String} formId - the form id
 * @param {Object} configuration - the configuration object from the portal redux state
 * @returns {Array} portalConfigurations
 * */
export const getPortalConfigurationLabels = (
  formId: string,
  configuration: ReduxPortalConfig
): Array<string | undefined> => {
  const configurationEntries = Object.entries(configuration)
  const portalConfigurations = configurationEntries
    .filter(
      ([key, value]) =>
        value === formId && basePortalConfig?.[key]?.type === 'formId'
    )
    .map(([key, value]) => {
      return basePortalConfig?.[key]?.label
    })
  return portalConfigurations
}

interface UseFormUsageReturn {
  getFormUsage: (args: {
    formId: string
    configuration: ReduxPortalConfig
    langVersion: string
  }) => JSX.Element | null
}

/** This hook is used to get a function that returns a JSX element that displays the names of the places that a form is available in.
 * @returns {Function} getFormUsage
 * */
export const useFormUsage = (): UseFormUsageReturn => {
  // state to store application type configurations
  const [configurations, setConfigurations] = useState<Configuration[]>([])

  // fetch application type configuration data, filter, map, and store in state
  useEffect(() => {
    fetchData().catch((e) => console.error(e))
  }, [])

  const fetchData = async (): Promise<any> => {
    await queryGetObjects({
      type: 'Configuration'
    }).then(configurations => {
      const filtered = filterConfigurations(configurations).map(item => {
        let data = {}
        if (isValidJSON(item.value)) {
          data = JSON.parse(item.value) ?? {}
        }
        return {
          ...item,
          ...data
        }
      })
      setConfigurations(filtered)
    })
  }

  /** returns application type configurations that include the formId
   * @param {String} formId - the form id
   * @returns {Array} applicationTypeConfigurations
   * */
  const getApplicationTypeConfigurations = (formId: string): Configuration[] => {
    if (formId === '' || formId == null) return []
    return configurations.filter(item => {
      return applicationTypeConfigurationFormFields.find(
        field => item[field] === formId
      )
      // return item.form === formId
    })
  }

  /** This function returns a JSX element that displays the names of the places that a form is available in.
   * @param {String} formId - the form id
   * @param {Configuration} configuration - the configuration object from the portal redux state
   * @param {String} langVersion - the language version
   * @returns {String} used places
   * */
  const getFormUsage = ({
    formId,
    configuration,
    langVersion
  }: GetFormUsageParams): ReactElement | null => {
    const applicationTypeConfigurations =
      getApplicationTypeConfigurations(formId)

    const applicationTypeUsage = applicationTypeConfigurations.map((item, index) => {
      const name = item?.name?.[langVersion] ??
        (typeof item.name === 'object' ? item.name?.en : item.name)
      const types = applicationTypeConfigurationFormFields.filter(
        field => item[field] === formId
      )
      const typesLabels = types.map((type, idx) => {
        return (
          <div key={idx}>
            {applicationTypeConfigurationFormFieldNames[type]}
            {idx !== types.length - 1 && ', '}
          </div>
        )
      })
      return (
        <li key={index}>
          {name} [{typesLabels}]
        </li>
      )
    })

    const portalConfigurationLabels = getPortalConfigurationLabels(
      formId,
      configuration
    )
    const usedPlaces = (
      <ul>
        {portalConfigurationLabels.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
        {applicationTypeUsage}
      </ul>
    )
    return portalConfigurationLabels.length > 0 ||
      applicationTypeUsage.length > 0
      ? usedPlaces
      : null
  }

  return { getFormUsage }
}
